/**
 * useAutoAdvanceAfterSave — the shared guest auto-advance effect.
 *
 * Both checkout pages, after a NEW card is saved, auto-advance a guest to the
 * next step (authenticated users advance via their explicit CTA). This extracts
 * that identical effect. The page keeps ownership of `paymentSaved`/`isProcessing`
 * and passes their setters in — the page's mutation `onError`s reset the same
 * flags, so ownership must stay page-side (this hook only runs the effect).
 *
 * @module features/payment/hooks/useAutoAdvanceAfterSave
 */

import { useEffect } from 'react';
import { butterToast } from '@butter/ui/patterns';
import { reportPaymentClientError } from '~/services/internalApi/payment/monitoring';

export interface UseAutoAdvanceAfterSaveParams {
  /** Set true by the page's `handlePaymentSuccess` once a new card is stored. */
  paymentSaved: boolean;
  setPaymentSaved: (value: boolean) => void;
  setIsProcessing: (value: boolean) => void;
  /** A guest exists (truthy) — only guests auto-advance. */
  guestUser: unknown;
  isAuthenticated: boolean;
  /**
   * A payment method is stored in the page's context (booking checks
   * `paymentMethodToken`, subscription `paymentMethodId`).
   */
  hasSavedPaymentMethod: boolean;
  /** Advance to the next checkout step; returns false when validation blocks it. */
  goToNextStep: () => boolean;
  /** Telemetry surface, e.g. `web_booking` | `web_subscription`. */
  surface: string;
}

export function useAutoAdvanceAfterSave({
  paymentSaved,
  setPaymentSaved,
  setIsProcessing,
  guestUser,
  isAuthenticated,
  hasSavedPaymentMethod,
  goToNextStep,
  surface,
}: UseAutoAdvanceAfterSaveParams): void {
  useEffect(() => {
    if (paymentSaved && guestUser && hasSavedPaymentMethod && !isAuthenticated) {
      const success = goToNextStep();
      if (!success) {
        reportPaymentClientError(
          'auto_advance_after_save',
          new Error('Auto-advance validation failed after saving payment method'),
          { kind: 'navigation', level: 'warning', surface }
        );
        butterToast.error('Unable to proceed. Please check your payment details.');
      }
      setPaymentSaved(false);
      setIsProcessing(false);
    } else if (paymentSaved && isAuthenticated) {
      setPaymentSaved(false);
      setIsProcessing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentSaved, guestUser, hasSavedPaymentMethod, isAuthenticated]);
}
